Ext.define('SA.lq.Model', {
    alias: 'view.locationQuery',
    extend: 'SA.data.View',

    statics: {
        create(params) {
            var model = new SA.lq.Model(params),
                firstSelectableLocation = model.getState().getSelectableLocations()[0] ?? null,
                locationItem = SA.data.LocationsCache.toLocationItem(firstSelectableLocation?.getStringId() ?? null);

            model.resolveState({filter: null, filterVisibility: 'disable', locationItem});

            return model;
        }
    },

    constructor() {
        this.callParent(arguments);

        this.projectAttributesProxy = this.project.getAttributesProxy(() => this.resolveState());
        this.projectLocationsProxy = this.project.getLocationsProxy(() => this.resolveState());
        this.locationsCacheAliasListener = SA.data.LocationAliases.getListener(aliasChange => this.onLocationsCacheAliasChange(aliasChange));
        this.customLocationsChangeListener = SA.data.CustomLocations.getChangeListener(() => this.onCustomLocationChange());
        this.enabledGeoUnitsChangeListener = this.project.getEnabledGeoUnitsChangeListener(() => this.onEnabledGeoUnitsChange());

        this.dataFactory = new SA.lq.ModelDataFactory({
            maxConditions: 12,
            defaultSortTuple: ['asc', 'name'],
            geoUnits: this.getCensusReleaseGeoUnits(),
            conditionDeserializer: new SA.model.ConditionDeserializer({modelFactory: new SA.model.ConditionFactory()})
        });

        this.geoUnits = new SA.model.GeoUnits({
            geoUnits: this.getCensusReleaseGeoUnits(),
            enabledIds: this.project.getEnabledGeoUnitIds()
        });

        this.locationPredicates = new SA.model.LocationPredicates({
            censusReleaseYear: this.getCensusReleaseYear(),
            geoUnits: this.geoUnits,

            predicates: [
                new SA.model.LocationHasCensusReleaseYearPredicate(),
                new SA.model.LocationHasEnabledSubGeoUnitsPredicate()
            ]
        });

        this.resolveState();
    },

    addAttributeToProject(attributeId) {
        this.projectAttributesProxy.addAttribute(attributeId);

        this.resolveState();
    },

    addLocationToProject(location) {
        this.projectLocationsProxy.addLocation(location);

        this.resolveState();
    },

    createController() {
        var locationAliasListener = SA.ListenerFactory.create(this, 'locationAliasChange'),
            customLocationListener = SA.ListenerFactory.create(this, 'customLocationChange');

        return SA.lq.ControllerFactory.createController({model: this, locationAliasListener, customLocationListener});
    },

    getLocations() {
        return Ext.Array.from(SA.data.LocationsCache.toLocationSeries(this.data.locationItem));
    },

    getState() {
        return this.state;
    },

    getSupportedLocationGeoUnitIds() {
        return this.geoUnits.getUnitsWithEnabledSubUnits().map(unit => unit.getId());
    },

    isSavable() {
        return this.getState().isSavable();
    },

    onCustomLocationChange() {
        this.resolveState();
        this.fireEvent('customLocationChange');
    },

    onEnabledGeoUnitsChange() {
        this.geoUnits.setEnabledIds(this.project.getEnabledGeoUnitIds());
        this.resolveState();
    },

    onLocationsCacheAliasChange(aliasChange) {
        this.resolveState();
        this.fireEvent('locationAliasChange', aliasChange);
    },

    onRemove() {
        this.callParent();

        this.projectAttributesProxy.destroy();
        this.projectLocationsProxy.destroy();
        this.locationsCacheAliasListener.destroy();
        this.customLocationsChangeListener.destroy();
        this.enabledGeoUnitsChangeListener.destroy();
    },

    removeLocation() {
        this.resolveState();
    },

    resolveState(data = {}) {
        var data = this.dataFactory.create({...this.data, ...data}),
            sort = data.getSortModel(),
            geoUnitId = data.getGeoUnitId(),
            conditionList = data.getConditionList(),
            selectableAttributes = this.project.getAttributeModels().filter(attribute => this.supportsAttribute(attribute.getId())),
            selectableAttributeIds = selectableAttributes.map(attribute => attribute.getId()),
            filteredLocations = this.locationPredicates.filter(this.project.getLocationModels()),
            resolvedLocation = filteredLocations.find(location => location.hasStringId(data.getLocationStringId())) ?? null,
            selectableGeoUnits = resolvedLocation ? this.geoUnits.getEnabledSubGeoUnitsForLocation(resolvedLocation) : [],
            resolvedGeoUnit = selectableGeoUnits.find(unit => unit.hasId(geoUnitId)) ?? selectableGeoUnits[0] ?? null,
            resolvedConditionList = new SA.model.ConditionFactory().createList({
                operator: conditionList.getOperator(),
                conditions: conditionList.getConditions().filter(condition => selectableAttributeIds.includes(condition.getAttributeId()))
            }),
            resolvedSort = (sort.getAttributeId() === 'name' || resolvedConditionList.hasAttributeId(sort.getAttributeId()))
                ? sort
                : SA.model.DataSort.asc('name');

        this.state = new SA.lq.ModelState({
            selectableGeoUnits,
            selectableAttributes,
            selectableLocations: filteredLocations,

            data: this.dataFactory.create({
                ...data.unwrap(),
                sort: resolvedSort.toTuple(),
                query: resolvedConditionList.serialize(),
                geographicUnit: resolvedGeoUnit?.getId() ?? null,
                locationItem: SA.data.LocationsCache.toLocationItem(resolvedLocation?.getStringId() ?? null)
            })
        });

        this.data = this.state.getData().unwrap();
        this.save();
    },

    selectGeoUnit(geoUnitId) {
        this.resolveState({geographicUnit: geoUnitId});
    },

    selectLocation(locationStringId) {
        SA.data.RecentLocations.add(locationStringId);

        this.projectLocationsProxy.addLocation(locationStringId);

        this.resolveState({locationItem: SA.data.LocationsCache.toLocationItem(locationStringId)});
        this.fireEvent('change:location');
    },

    setConditionList(conditionList) {
        this.projectAttributesProxy.addAttributes(conditionList.getUniqueAttributeIds());

        this.resolveState({query: conditionList.serialize()});
    },

    setSort(sort) {
        this.resolveState({sort: sort.toTuple()});
    },

    supportsAttribute(attributeId) {
        var attribute = SA.data.AttributesCache.getModel(attributeId),
            matchesCensusReleaseYear = attribute.hasCensusReleaseYear(this.getCensusReleaseYear());

        return matchesCensusReleaseYear && attribute.isPolygonAttribute();
    },

    supportsLocation(locationValue) {
        return this.locationPredicates.testValue(locationValue);
    }
});